import type { ChatStreamEvent, WireConversationSummary } from '@logistics/contracts';
import { ago, monthDay } from '../lib/format';

/** Everything the chat writes on screen, as pure functions of what the API returned. */

export type StepEvent = Extract<ChatStreamEvent, { type: 'step' }>;

/** What the thinking indicator says while the agent works. */
export function stepText(step: StepEvent | undefined): string {
  if (step === undefined || step.step === 'reading') return 'Reading the question…';
  if (step.step === 'searching') {
    const where = step.label ?? 'your sources';
    const what = step.subject === undefined || step.subject === '' ? '' : ` for ${step.subject}`;
    return `Searching ${where}${what}…`;
  }
  if (step.step === 'tool') {
    const what = step.subject === undefined || step.subject === '' ? '' : ` for ${step.subject}`;
    const tool = step.tool ?? 'data';
    return step.label === undefined ? `Checking ${tool}${what}…` : `${step.label}: ${tool}${what}…`;
  }
  if (step.records === undefined) return 'Writing the answer…';
  return `Writing the answer from ${step.records} ${step.records === 1 ? 'record' : 'records'}…`;
}

/**
 * The typewriter's units: words with the whitespace after them, so joining any
 * prefix gives exactly the start of the text — line breaks and all.
 */
export function splitWords(text: string): string[] {
  return text.match(/\S+\s*|\s+/g) ?? [];
}

/** How many words each tick reveals: two, or more for a long answer so it never drags past ~4 s. */
export function wordsPerTick(total: number): number {
  return Math.max(2, Math.ceil(total / 150));
}

/**
 * A list row's second line: "Anna · Sep 17". An admin sees everyone's
 * conversations, so the owner is always named; your own read "You".
 */
export function conversationMeta(
  conversation: Pick<WireConversationSummary, 'owner' | 'updatedAt'>,
  meId: string,
  at: Date,
): string {
  const who = conversation.owner.id === meId ? 'You' : firstName(conversation.owner.name);
  const minutes = (at.getTime() - new Date(conversation.updatedAt).getTime()) / 60_000;
  const when = minutes < 60 ? ago(conversation.updatedAt, at) : monthDay(conversation.updatedAt);
  return `${who} · ${when}`;
}

function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name;
}

/** The thread header's title: at most 38 characters, as the server titles them. */
export function threadTitle(title: string | undefined): string {
  if (title === undefined || title.trim() === '') return 'New conversation';
  return title.length > 40 ? `${title.slice(0, 38)}…` : title;
}

/** "all sources" or "2 of 4 sources", counted against what is available here. */
export function scopeSummary(selected: number, available: number): string {
  if (available === 0) return 'no sources connected';
  if (selected >= available) return 'all sources';
  return `${selected} of ${available} sources`;
}

/** A citation chip's age, without the "ago" the chip has no room for. */
export function citationAge(retrievedAt: string, at: Date): string {
  const text = ago(retrievedAt, at);
  return text.replace(/ ago$/, '');
}

/** "3 records · Samsara" — what hovering a citation chip explains. */
export function citationDetail(count: number, source: string): string {
  return `${count} ${count === 1 ? 'record' : 'records'} · ${source}`;
}

/** The card's columns: list and thread, plus the artifact — without the list when narrow. */
export function chatColumns(artifactOpen: boolean, listHidden: boolean): string {
  if (!artifactOpen) return '220px minmax(0,1fr)';
  return listHidden
    ? 'minmax(300px,1fr) minmax(300px,0.9fr)'
    : '220px minmax(320px,1fr) minmax(320px,380px)';
}
